import React from 'react';

import { FormattedMessage } from 'react-intl-next';

import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	ITEM_FIELDS as CARD_FIELDS,
	RenderVariant,
	RenderVariantSwitcherDropdown,
	SegmentationTargetFieldV2,
	SegmentationTargetGroupsField,
	SegmentationTargets,
} from '@atlassian/company-hub-common';

import type { CardsActions } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';
import { type Card } from '../../../../types';

import { messages } from './messages';

const wrapperStyles = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	paddingBottom: 'space.200',
});

const styles = xcss({
	marginBottom: 'space.050',
});

export type CardsConfigContentSectionMainProps = {
	renderVariant: RenderVariant;
	segmentationTarget?: SegmentationTargets;
	audienceSegmentationGroupIds?: string[];
	extensionActions: CardsActions;
	isCompanyHub: boolean;
	updateRenderVariant: (renderVariant: RenderVariant) => void;
	cards: Card[];
	source: string;
};

export const CardsConfigContentSectionMain = ({
	segmentationTarget,
	audienceSegmentationGroupIds,
	renderVariant,
	extensionActions,
	isCompanyHub,
	updateRenderVariant,
	cards,
	source,
}: CardsConfigContentSectionMainProps) => {
	const { updateMainFormField, updateCardFormFieldForAllCards } = extensionActions;

	const isCompanyHubAndSegmentationEnabledForCardVariant =
		isCompanyHub &&
		(renderVariant === RenderVariant.MANUAL ||
			fg('company-hub-dynamic-cards-audience-segmentation'));

	const shouldShowSegmentationTargetGroupsField =
		segmentationTarget === SegmentationTargets.ALL &&
		isCompanyHubAndSegmentationEnabledForCardVariant;

	const handleGroupSelection = (selectedGroups: string[] | undefined) => {
		if (fg('company-hub-dynamic-cards-audience-segmentation')) {
			updateMainFormField({ audienceSegmentationGroupIds: selectedGroups });
		} else {
			updateCardFormFieldForAllCards({
				[CARD_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]: selectedGroups,
			});
		}
	};

	return (
		<Stack xcss={wrapperStyles} space="space.200">
			<RenderVariantSwitcherDropdown
				renderVariant={renderVariant}
				onUpdate={updateRenderVariant}
				source={source}
			/>
			{isCompanyHubAndSegmentationEnabledForCardVariant && (
				<Box xcss={styles}>
					<Lozenge appearance="new">
						<FormattedMessage {...messages.newLozengeLabel} />
					</Lozenge>
					<SegmentationTargetFieldV2
						audienceSegmentationGroupIds={audienceSegmentationGroupIds}
						updateMainFormField={updateMainFormField}
						targetParameter={segmentationTarget}
						updateCardFormFieldForAllCards={updateCardFormFieldForAllCards}
						renderVariant={renderVariant}
						source={source}
					/>
				</Box>
			)}
			{shouldShowSegmentationTargetGroupsField && (
				<SegmentationTargetGroupsField
					targetGroupsIds={
						segmentationTarget === SegmentationTargets.ALL &&
						fg('company-hub-dynamic-cards-audience-segmentation')
							? audienceSegmentationGroupIds
							: cards[0]?.audienceSegmentationGroupIds
					}
					onGroupSelected={handleGroupSelection}
					showUpdatedStyles
					source={source}
				/>
			)}
		</Stack>
	);
};
